import * as comlink from "comlink"
import { Zip, ZipPassThrough } from "fflate"

import type { SandboxDownloadFile } from "~/models/polycache/SandboxDownloadFile"

import { EntityMimeTypeId, PropertyId, stringToMimeType } from "../entity"
import { StaticFile } from "../file/StaticFile"
import { createFileDownloadRequestDatagram } from "../handlers/file/download"

/** On browser, we need an additional "finalizing" step since we package the file to zip */
export type DownloadJobStatus = "preparing" | "downloading" | "failed" | "finalizing" | "complete"
export type DownloadJobFileStatus = "downloading" | "downloaded" | "complete" | "failed"

export interface DownloadJobState {
  type: "download"
  transferId: string
  status: DownloadJobStatus
  title?: string
  sources: Record<string, StaticFile>
  timestamp: number
  approxTotal: number
  approxTotalBytes: number
  files: Record<string, {
    status: DownloadJobFileStatus
    fileId: string
    totalBytes: number
    completedBytes: number
  }>
}

export class DownloadJob {
  state: Ref<DownloadJobState>

  constructor(state: Ref<DownloadJobState>) {
    this.state = state
  }

  started(start: DownloadStartedStatusDatagram) {
    const { transferId, approxTotal, approxTotalBytes } = start
    if (transferId !== this.state.value.transferId)
      return console.error("This update doesn't match", start)
    this.state.value.status = "preparing"
    this.state.value.approxTotal = approxTotal
    this.state.value.approxTotalBytes = Number(approxTotalBytes)
  }

  static addFromStartDatagram(start: DownloadStartedStatusDatagram) {
    const { state } = useJobs()
    const job: DownloadJobState = {
      type: "download",
      transferId: start.transferId,
      status: "preparing",
      sources: {},
      timestamp: Date.now(),
      approxTotal: start.approxTotal,
      approxTotalBytes: Number(start.approxTotalBytes),
      files: {},
    }
    state.value.jobs[job.transferId] = job
    return new DownloadJob(toRef(state.value.jobs, job.transferId) as Ref<DownloadJobState>)
  }

  updated(update: DownloadProgressStatusDatagram) {
    const { transferId, approxTotal, approxTotalBytes } = update
    if (transferId !== this.state.value.transferId)
      return console.error("This update doesn't match", update)

    this.state.value.status = "downloading"
    this.state.value.approxTotal = approxTotal
    this.state.value.approxTotalBytes = Number(approxTotalBytes)
  }

  fileUpdated(update: DownloadFileProgressStatusDatagram) {
    this.state.value.status = "downloading"

    let status: DownloadJobFileStatus = "downloading"
    // on desktop there's no zip assembly, so we go straight to completed
    if (isDesktop())
      status = "complete"
    else if (update.completedBytes === update.totalBytes)
      status = "downloaded"

    this.state.value.files[update.fileId] = {
      status,
      fileId: update.fileId,
      totalBytes: Number(update.totalBytes),
      completedBytes: Number(update.completedBytes),
    }
  }

  fileFailed(update: DownloadFileFailedStatusDatagram) {
    if (update.transferId !== this.state.value.transferId)
      return console.error("This update doesn't match", update)
    if (!isDefined(this.state.value.files[update.file.id]))
      return console.error("This update references an unknown file", update)

    this.state.value.files[update.file.id].status = "failed"
    this.state.value.files[update.file.id].completedBytes = this.state.value.files[update.file.id].totalBytes
  }

  finished(finished: DownloadFinishedStatusDatagram) {
    const { transferId, approxTotal, approxTotalBytes,
    } = finished
    if (transferId !== this.state.value.transferId)
      return console.error("This update doesn't match", finished)

    this.state.value.approxTotal = approxTotal
    this.state.value.approxTotalBytes = Number(approxTotalBytes)

    // TODO: how to tell if failed?
    // We set to finalizing here since on browser we need to construct the zip file
    this.state.value.status = finished.success ? "finalizing" : "failed"
  }

  progressDetails = () => {
    return {
      finishedBytes: this.finishedBytes.value,
      successBytes: this.completedBytes.value,
      failedBytes: this.failedBytes.value,
      totalBytes: this.state.value.approxTotalBytes,
    }
  }

  #bytesByType = (type: DownloadJobFileStatus) => computed(() => Object
    .values(this.state.value.files)
    .filter(f => f.status === type)
    .map(f => f.totalBytes)
    .reduce((a, b) => a + b, 0))

  totalFiles = computed(() => Object.values(this.state.value.files).length)
  totalBytes = computed(() => Object.values(this.state.value.files).map(f => f.totalBytes).reduce((a, b) => a + b, 0))
  /** This includes all uploaded, completed, or failed bytes */
  finishedBytes = computed(() => Object.values(this.state.value.files).map(f => f.completedBytes).reduce((a, b) => a + b, 0))

  /** This is, confusingly, all bytes of files in "downloading" state */
  downloadingBytes = this.#bytesByType("downloading")
  downloadedBytes = this.#bytesByType("downloaded")
  completedBytes = this.#bytesByType("complete")
  failedBytes = this.#bytesByType("failed")
}

export class BrowserDownloadJob extends DownloadJob {
  #properties = [
    PropertyId.FILE_SHA256_CHECKSUM,
    PropertyId.FILE_MIME_TYPE,
    PropertyId.FILE_SIZE,
    PropertyId.FILE_NAME,
    PropertyId.LINK_DESTINATION_URL,
    PropertyId.FOLDER_USAGE_QUANTITY,
    PropertyId.FOLDER_USAGE_SIZE,
    PropertyId.FOLDER_USAGE_UPDATED_AT,
  ]

  async startDownload() {
    const { cacheWorker } = useCache()
    const { sandboxWorker } = useSandbox()
    const requestId = generateWorkerRequestId()

    // TODO: we only need the folder derived, mime, and size, so we could optimize
    // that here if we wanted.
    const requests: FileDownloadRequestDatagram[] = Object
      .values(this.state.value.sources)
      .map((f) => {
        const file = StaticFile.blank(f.id)
        for (const p of Object.values(f.properties))
          if (isDefined(p) && this.#properties.includes(p.propertyId as PropertyId))
            file.addProperty(p)
        return {
          requestId,
          offline: false,
          file: file.inner(),
          onlineOnly: false,
        }
      },
      )
    const bytes = createFileDownloadRequestDatagram(requests)

    const files: SandboxDownloadFile[] = await cacheWorker().downloadItems(
      this.state.value.transferId,
      comlink.transfer(bytes, [bytes.buffer]), // makes the arraybuffers zero-copy
    )

    console.debug("Received sandbox download files", files)

    if (files.length === 1) {
      const file = files[0]
      const buffer = await sandboxWorker().downloadCachedFile(file)

      // Now we can mark the job as complete
      this.state.value.status = "complete"

      const a = document.createElement("a")
      const blob = new Blob([buffer])
      a.href = URL.createObjectURL(blob)
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(a.href)
      return
    }

    // Create a timestamp in the format YYYY-MM-DD HH-MM-SS
    const name = `Poly files.zip`

    const buffers: ArrayBuffer[] = []
    const { resolve, reject, promise } = Promise.withResolvers<void>()
    const zip = new Zip((err, blob, final) => {
      if (err)
        reject(err)
      // @ts-expect-error blob is ArrayBuffer
      buffers.push(blob.buffer)
      if (!final)
        return
      resolve()
    })

    const addFile = async (file: SandboxDownloadFile) => {
      const mimeType = stringToMimeType(file.mimeType)
      if (mimeType.value === EntityMimeTypeId.TextUriList)
        return console.warn("Link file types not supported in browser downloads")
      else if (mimeType.value === EntityMimeTypeId.InodeDirectory)
        return
      const path = [file.relativePath, file.name].join("")
      const buffer = await sandboxWorker().downloadCachedFile(file)
      const bytes = new Uint8Array(buffer)
      const zipFile = new ZipPassThrough(path)
      zip.add(zipFile)
      zipFile.push(bytes, true)

      // now that the file is done, we can mark the file as completed
      if (!isDefined(this.state.value.files[file.fileId]))
        console.warn("File not found in job state", file.fileId)
      else
        this.state.value.files[file.fileId].status = "complete"
    }

    await Promise.all(files.map(addFile))
    zip.end() // calling end causes the `final` to be true in the callback
    await promise
    const blob = new Blob(buffers)

    this.state.value.status = "complete"

    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(a.href)
  }
}

export class DesktopDownloadJob extends DownloadJob {
  async startDownload() {
    const { state: myself } = useSelf()
    const { downloadFiles } = useTauri()
    if (!myself.value.user)
      return console.error("Cannot upload while logged out")
    const requestId = generateWorkerRequestId()
    const requests: FileDownloadRequestDatagram[] = Object
      .values(this.state.value.sources)
      .map(f => ({
        requestId,
        offline: false,
        file: f.inner(),
        onlineOnly: false,
      }))
    const bytes = createFileDownloadRequestDatagram(requests)
    await downloadFiles({
      transferId: this.state.value.transferId,
      bytes,
    })

    this.state.value.status = "complete"
    console.debug("Download complete")
  }
}
